import { useTranslation } from "react-i18next";
import { getLocalizedParanoidiPlans } from "./paranoidiPlans";

export default function ParanoidiPricingCards({
  selectedPlan = "",
  onSelectPlan = null,
  showExcluded = true,
  compact = false,
  ctaLabel = "",
}) {
  const { t } = useTranslation("admin");
  const plans = getLocalizedParanoidiPlans(t);

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {plans.map((plan) => {
        const isSelected = selectedPlan === plan.id;
        const isClickable = typeof onSelectPlan === "function";

        const CardTag = isClickable ? "button" : "article";

        return (
          <CardTag
            key={plan.id}
            type={isClickable ? "button" : undefined}
            onClick={isClickable ? () => onSelectPlan(plan.id) : undefined}
            className={[
              "flex h-full flex-col rounded-[2rem] border p-6 text-left transition sm:p-8",
              isClickable ? "hover:-translate-y-0.5 active:scale-[0.99]" : "",
              plan.featured
                ? "border-red-400/50 bg-red-500/10 shadow-2xl shadow-red-950/30"
                : "border-white/10 bg-white/[0.035]",
              isSelected ? "ring-2 ring-red-400" : "",
            ].join(" ")}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                {plan.badge && (
                  <p className="mb-4 inline-flex w-fit rounded-full bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-white">
                    {plan.badge}
                  </p>
                )}

                <h3 className="text-2xl font-black uppercase tracking-[-0.04em] text-white">
                  {plan.name}
                </h3>
              </div>

              {isClickable && (
                <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-400">
                  {isSelected
                    ? t("paranoidiPricingCards.selected")
                    : t("paranoidiPricingCards.select")}
                </p>
              )}
            </div>

            <p className="mt-3 text-xl font-black text-red-300">
              {plan.price}
            </p>

            <p className="mt-4 text-sm font-semibold leading-7 text-zinc-400">
              {plan.description}
            </p>

            <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-zinc-500">
                {t("paranoidiPricingCards.songLimit")}
              </p>

              <p className="mt-1 text-xl font-black text-white">
                {t("paranoidiPricingCards.songCount", {
                  count: plan.songLimit,
                })}
              </p>
            </div>

            <ul
              className={[
                "mt-6 grid gap-3",
                compact ? "text-xs" : "text-sm",
              ].join(" ")}
            >
              {plan.included.map((item) => (
                <li
                  key={item}
                  className="flex gap-3 font-bold leading-6 text-zinc-200"
                >
                  <span className="mt-0.5 shrink-0 text-red-400">✓</span>
                  <span>{item}</span>
                </li>
              ))}

              {showExcluded &&
                plan.excluded.map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 font-bold leading-6 text-zinc-500 line-through decoration-zinc-600"
                  >
                    <span className="mt-0.5 shrink-0 text-zinc-600">–</span>
                    <span>{item}</span>
                  </li>
                ))}
            </ul>

            {ctaLabel && (
              <div className="mt-auto pt-8">
                <span
                  className={[
                    "inline-flex w-full items-center justify-center rounded-full px-6 py-4 text-sm font-black uppercase tracking-[0.14em]",
                    plan.featured
                      ? "bg-red-500 text-white"
                      : "bg-white text-black",
                  ].join(" ")}
                >
                  {ctaLabel}
                </span>
              </div>
            )}
          </CardTag>
        );
      })}
    </div>
  );
}
